import React, { useRef, useEffect, useState } from 'react';
import { X, ChevronDown, Info } from 'lucide-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  createOutageRequisition,
  updateOutageRequisition,
  getUnitsDropdown,
  getOutageRequisitionDetail,
  getOutageTypesDropdown,
} from '../../../services/outageRequisitionSevice';
import { getSites } from '../../../services/sitesService';
import { getUsersDropdown } from '../../../services/userService';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';
import TimePicker from '../../../components/TimePicker/TimePicker';

type Props = {
  onClose: () => void;
  onSubmitSuccess?: () => void;
  onError?: (message: string) => void;
  editId?: string | null;
};

const MAX_LENGTH = 1000;

/* -------------------- Validation -------------------- */
const validationSchema = Yup.object({
  site: Yup.string().required('Site is required'),
  person: Yup.string().required('Person is required'),
  requisitionDate: Yup.string().required('Date of requisition is required'),
  unit: Yup.string().required('Unit is required'),
  reason: Yup.string().required('Reason for outage is required'),
  outageStartDate: Yup.string().required('Outage start date is required'),
  outageEndDate: Yup.string()
    .required('Outage end date is required')
    .test('not-before-start', 'End date cannot be before the start date', function (value) {
      return !value || !this.parent.outageStartDate || value >= this.parent.outageStartDate;
    }),
  startTime: Yup.string().required('Start time is required'),
  endTime: Yup.string()
    .required('End time is required')
    .test('end-time-after-start-time', 'End time must be later than start time', function (value) {
      const { outageStartDate, outageEndDate, startTime } = this.parent;

      // Only enforce this when start and end fall on the same calendar day
      if (!value || !startTime || !outageStartDate || !outageEndDate) return true;
      if (outageStartDate !== outageEndDate) return true;

      return value > startTime;
    }),
  outageType: Yup.string().required('Outage type is required'),
});

const OutageRequisitionModal: React.FC<Props> = ({ onClose, onSubmitSuccess, onError, editId }) => {
  const isEditMode = Boolean(editId);
  const requisitionDateRef = useRef<HTMLInputElement>(null);
  const outageStartDateRef = useRef<HTMLInputElement>(null);
  const outageEndDateRef = useRef<HTMLInputElement>(null);

  const [sites, setSites] = useState<{ id: number; name: string }[]>([]);
  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
  const [units, setUnits] = useState<{ id: number; title: string }[]>([]);
  const [outageTypes, setOutageTypes] = useState<{ id: number; title: string }[]>([]);

  const siteRef = useRef<HTMLSelectElement>(null);
  const personRef = useRef<HTMLInputElement>(null);
  const unitRef = useRef<HTMLSelectElement>(null);
  const outageTypeRef = useRef<HTMLSelectElement>(null);

  const [personSearch, setPersonSearch] = useState('');
  const [showPersonDropdown, setShowPersonDropdown] = useState(false);

  const [initialValues, setInitialValues] = useState({
    site: '',
    person: '',
    requisitionDate: '',
    unit: '',
    reason: '',
    outageStartDate: '',
    outageEndDate: '',
    startTime: '',
    endTime: '',
    isolations: '',
    systemConstraints: '',
    previousOutageRemarks: '',
    outageType: '',
  });

  const getTodayDate = () => new Date().toISOString().slice(0, 10);

  /* -------------------- HELPER FUNCTION -------------------- */
  const findIdByText = (array: any[], textValue: string, fields: string[]) => {
    if (!textValue || !array.length) return '';
    const item = array.find((el) => fields.some((field) => el[field]?.toLowerCase() === textValue.toLowerCase()));
    return item ? String(item.id) : '';
  };

  const quillRef = useRef<ReactQuill>(null);
  const isolationsQuillRef = useRef<ReactQuill>(null);
  const systemConstraintsQuillRef = useRef<ReactQuill>(null);
  const previousRemarksQuillRef = useRef<ReactQuill>(null);

  const scrollToField = (element: HTMLElement | null) => {
    if (!element) return;

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    setTimeout(() => {
      element.focus();
    }, 300);
  };

  /* -------------------- FETCH SITES -------------------- */
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getSites();
        setSites(Array.isArray(response?.data) ? response.data : []);
      } catch (err: any) {
        console.error('Failed to fetch sites:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load sites';
        onError?.(errorMessage);
      }
    };
    fetchSites();
  }, []);

  /* -------------------- FETCH USERS -------------------- */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsersDropdown();
        setUsers(Array.isArray(response?.data) ? response.data : []);
      } catch (err: any) {
        console.error('Failed to fetch users:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load users';
        onError?.(errorMessage);
      }
    };
    fetchUsers();
  }, []);

  /* -------------------- FETCH UNITS -------------------- */
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await getUnitsDropdown();
        setUnits(Array.isArray(response) ? response : []);
      } catch (err: any) {
        console.error('Failed to fetch units:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load units';
        onError?.(errorMessage);
      }
    };
    fetchUnits();
  }, []);

  /* -------------------- FETCH OUTAGE TYPES -------------------- */
  useEffect(() => {
    const fetchOutageTypes = async () => {
      try {
        const response = await getOutageTypesDropdown();
        setOutageTypes(Array.isArray(response) ? response : []);
      } catch (err: any) {
        console.error('Failed to fetch outage types:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load outage types';
        onError?.(errorMessage);
      }
    };
    fetchOutageTypes();
  }, []);

  /* -------------------- FETCH EDIT DATA -------------------- */
  useEffect(() => {
    if (!editId || !sites.length || !users.length || !units.length || !outageTypes.length) return;

    const fetchEditData = async () => {
      try {
        const response = await getOutageRequisitionDetail(editId);
        const data = response.data?.data || response.data;

        const personId = data.user_id ? String(data.user_id) : findIdByText(users, data.user?.name || data.requester?.name, ['name']);

        setInitialValues({
          site: data.site_id ? String(data.site_id) : findIdByText(sites, data.site?.name, ['name']),
          person: personId,
          requisitionDate: data.date_of_requisition || '',
          unit: data.unit_id ? String(data.unit_id) : findIdByText(units, data.unit?.title, ['title']),
          reason: data.reason_for_outage || '',
          outageStartDate: data.outage_start_date || '',
          outageEndDate: data.outage_end_date || '',
          startTime: data.start_time?.slice(0, 5) || '',
          endTime: data.end_time?.slice(0, 5) || '',
          isolations: data.isolations || '',
          systemConstraints: data.system_constraints || '',
          previousOutageRemarks: data.previous_outage_remarks || '',
          outageType: data.outage_type_id
            ? String(data.outage_type_id)
            : data.outage_type && typeof data.outage_type === 'object'
              ? String(data.outage_type.id)
              : findIdByText(outageTypes, data.outage_type, ['title']),
        });

        // Pre-fill person search text for edit mode
        const selectedUser = users.find((u) => String(u.id) === personId);
        if (selectedUser) setPersonSearch(selectedUser.name);
      } catch (err: any) {
        console.error('Failed to fetch outage requisition detail:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load data for editing';
        onError?.(errorMessage);
        onClose();
      }
    };

    fetchEditData();
  }, [editId, sites, users, units, outageTypes]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      {/* <div className="bg-white w-full max-w-5xl rounded-xl shadow-lg overflow-hidden"> */}
      <div className="bg-white w-full max-w-5xl rounded-xl shadow-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-lg font-semibold">{isEditMode ? 'Edit Outage Requisition' : 'Add Outage Requisition'}</h2>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            const payload = {
              site_id: Number(values.site),
              user_id: Number(values.person),
              date_of_requisition: values.requisitionDate,
              unit_id: Number(values.unit),
              reason_for_outage: values.reason,
              outage_start_date: values.outageStartDate,
              outage_end_date: values.outageEndDate,
              start_time: values.startTime,
              end_time: values.endTime,
              isolations: values.isolations,
              system_constraints: values.systemConstraints,
              previous_outage_remarks: values.previousOutageRemarks,
              outage_type_id: Number(values.outageType),
            };

            try {
              if (isEditMode && editId) {
                await updateOutageRequisition(editId, payload);
              } else {
                await createOutageRequisition(payload);
              }
              onSubmitSuccess?.();
              onClose();
            } catch (err: any) {
              console.error('Error saving outage requisition:', err);
              const errorMessage =
                err?.response?.data?.message || err?.message || `Failed to ${isEditMode ? 'update' : 'create'} outage requisition`;
              onError?.(errorMessage);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting, validateForm, setTouched }) => (
            // <Form className="p-6 space-y-6">
            <Form className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-sm font-medium">Site</label> <span className="text-red-500">*</span>
                    <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </div>

                  <div className="relative">
                    <Field
                      innerRef={siteRef}
                      as="select"
                      name="site"
                      className={`form-select ${!values.site ? 'text-gray-400' : 'text-gray-700'} bg-gray-100`}
                    >
                      <option value="">Select Site</option>
                      {sites.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.name}
                        </option>
                      ))}
                    </Field>
                    <ChevronDown className="select-icon" />
                  </div>

                  <ErrorMessage name="site" component="p" className="error" />
                </div>

                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-sm font-medium">Name of Person Requiring Outage</label>
                    <span className="text-red-500">*</span>
                  </div>
                  <div className="relative">
                    <input
                      ref={personRef}
                      type="text"
                      placeholder="Search Person"
                      value={personSearch}
                      onChange={(e) => {
                        setPersonSearch(e.target.value);
                        setFieldValue('person', '');
                        setShowPersonDropdown(true);
                      }}
                      onFocus={() => setShowPersonDropdown(true)}
                      onBlur={() =>
                        setTimeout(() => {
                          setShowPersonDropdown(false);
                          const match = users.find((u) => u.name.toLowerCase() === personSearch.toLowerCase());
                          if (!match) {
                            setPersonSearch('');
                            setFieldValue('person', '');
                          }
                        }, 200)
                      }
                      className={`form-input pr-10 ${!personSearch ? 'text-gray-400' : 'text-gray-700'} bg-gray-100`}
                    />
                    <ChevronDown className="select-icon" />

                    {showPersonDropdown && (
                      <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                        {users.filter((u) => u.name.toLowerCase().includes(personSearch.toLowerCase())).length === 0 ? (
                          <div className="px-4 py-2 text-sm text-gray-500">No results found</div>
                        ) : (
                          users
                            .filter((u) => u.name.toLowerCase().includes(personSearch.toLowerCase()))
                            .map((user) => (
                              <div
                                key={user.id}
                                onMouseDown={() => {
                                  setFieldValue('person', String(user.id));
                                  setPersonSearch(user.name);
                                  setShowPersonDropdown(false);
                                }}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                              >
                                {user.name}
                              </div>
                            ))
                        )}
                      </div>
                    )}
                  </div>
                  <ErrorMessage name="person" component="p" className="error" />
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-1">
                    Date of Requisition <span className="text-red-500">*</span>
                  </label>
                  <StandardDatePicker
                    inputRef={requisitionDateRef}
                    value={values.requisitionDate}
                    min={getTodayDate()}
                    onChange={(value) => setFieldValue('requisitionDate', value)}
                  />
                  <ErrorMessage name="requisitionDate" component="p" className="error" />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1">Unit Requested</label> <span className="text-red-500">*</span>
                  <div className="relative">
                    <Field
                      innerRef={unitRef}
                      as="select"
                      name="unit"
                      className={`form-select ${!values.unit ? 'text-gray-400' : 'text-gray-700'} bg-gray-100`}
                    >
                      <option value="">Select Unit Requested</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.title}
                        </option>
                      ))}
                    </Field>
                    <ChevronDown className="select-icon" />
                  </div>
                  <ErrorMessage name="unit" component="p" className="error" />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="flex items-center gap-1 text-sm font-medium mb-2">
                  Reason for Outage <span className="text-red-500">*</span> <Info className="w-3 h-3 text-gray-400" />
                </label>

                <div className="bg-gray-100 border border-gray-300 rounded ">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={values.reason}
                    onChange={(value) => {
                      const editor = quillRef.current?.getEditor();
                      if (editor) {
                        const length = editor.getLength() - 1; // -1 for Quill's trailing newline
                        if (length > MAX_LENGTH) {
                          editor.deleteText(MAX_LENGTH, length - MAX_LENGTH);
                          return;
                        }
                      }
                      setFieldValue('reason', value);
                    }}
                    placeholder="Add description here"
                    className="bg-gray-100"
                  />
                </div>

                <div className="text-xs text-gray-400 mt-1 text-right">
                  {(quillRef.current?.getEditor()?.getLength() ?? 1) - 1}/{MAX_LENGTH}
                </div>

                <ErrorMessage name="reason" component="p" className="error mt-1" />
              </div>

              {/* Outage period */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-1">
                    Outage Start Date <span className="text-red-500">*</span>
                  </label>
                  <StandardDatePicker
                    inputRef={outageStartDateRef}
                    value={values.outageStartDate}
                    disabled={!values.requisitionDate}
                    min={values.requisitionDate}
                    onChange={(value) => {
                      setFieldValue('outageStartDate', value);
                      if (values.outageEndDate && values.outageEndDate < value) setFieldValue('outageEndDate', '');
                    }}
                  />
                  <ErrorMessage name="outageStartDate" component="p" className="error" />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1">
                    Outage End Date <span className="text-red-500">*</span>
                  </label>
                  <StandardDatePicker
                    inputRef={outageEndDateRef}
                    value={values.outageEndDate}
                    disabled={!values.outageStartDate}
                    min={values.outageStartDate}
                    onChange={(value) => setFieldValue('outageEndDate', value)}
                  />
                  <ErrorMessage name="outageEndDate" component="p" className="error" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <TimePicker value={values.startTime} onChange={(value) => setFieldValue('startTime', value)} />
                  <ErrorMessage name="startTime" component="p" className="error" />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <TimePicker value={values.endTime} onChange={(value) => setFieldValue('endTime', value)} />
                  <ErrorMessage name="endTime" component="p" className="error" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-1">
                    Outage Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Field
                      innerRef={outageTypeRef}
                      as="select"
                      name="outageType"
                      className={`form-select ${!values.outageType ? 'text-gray-400' : 'text-gray-700'} bg-gray-100`}
                    >
                      <option value="">Select Outage Type</option>
                      {outageTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.title}
                        </option>
                      ))}
                    </Field>
                    <ChevronDown className="select-icon" />
                  </div>
                  <ErrorMessage name="outageType" component="p" className="error" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Isolations</label>
                  <div className="bg-gray-100 border border-gray-300 rounded">
                    <ReactQuill
                      ref={isolationsQuillRef}
                      theme="snow"
                      value={values.isolations}
                      onChange={(value) => {
                        const editor = isolationsQuillRef.current?.getEditor();
                        if (editor) {
                          const length = editor.getLength() - 1;
                          if (length > MAX_LENGTH) {
                            editor.deleteText(MAX_LENGTH, length - MAX_LENGTH);
                            return;
                          }
                        }
                        setFieldValue('isolations', value);
                      }}
                      placeholder="Describe the required isolations"
                      className="bg-gray-100"
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {(isolationsQuillRef.current?.getEditor()?.getLength() ?? 1) - 1}/{MAX_LENGTH}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">System Constraints</label>
                  <div className="bg-gray-100 border border-gray-300 rounded">
                    <ReactQuill
                      ref={systemConstraintsQuillRef}
                      theme="snow"
                      value={values.systemConstraints}
                      onChange={(value) => {
                        const editor = systemConstraintsQuillRef.current?.getEditor();
                        if (editor) {
                          const length = editor.getLength() - 1;
                          if (length > MAX_LENGTH) {
                            editor.deleteText(MAX_LENGTH, length - MAX_LENGTH);
                            return;
                          }
                        }
                        setFieldValue('systemConstraints', value);
                      }}
                      placeholder="Describe any system constraints"
                      className="bg-gray-100"
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-1 text-right">
                    {(systemConstraintsQuillRef.current?.getEditor()?.getLength() ?? 1) - 1}/{MAX_LENGTH}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Remarks on Previous Unit Outage</label>
                <div className="bg-gray-100 border border-gray-300 rounded">
                  <ReactQuill
                    ref={previousRemarksQuillRef}
                    theme="snow"
                    value={values.previousOutageRemarks}
                    onChange={(value) => {
                      const editor = previousRemarksQuillRef.current?.getEditor();
                      if (editor) {
                        const length = editor.getLength() - 1;
                        if (length > MAX_LENGTH) {
                          editor.deleteText(MAX_LENGTH, length - MAX_LENGTH);
                          return;
                        }
                      }
                      setFieldValue('previousOutageRemarks', value);
                    }}
                    placeholder="Add remarks from previous outages"
                    className="bg-gray-100"
                  />
                </div>
                <div className="text-xs text-gray-400 mt-1 text-right">
                  {(previousRemarksQuillRef.current?.getEditor()?.getLength() ?? 1) - 1}/{MAX_LENGTH}
                </div>
              </div>
              {/* Note */}
              <div className="note-box">
                <h4 className="note-title">Note</h4>
                <p>1. Requester encouraged to discuss with shift supervisor to complete the form</p>
                <p>2. Outage requests on short notice (less than 21 hrs) fetch a penalty from UETCL</p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <button type="button" onClick={onClose} className="px-6 py-2 border rounded-md">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={async () => {
                    const errors = await validateForm();

                    setTouched({
                      site: true,
                      person: true,
                      requisitionDate: true,
                      unit: true,
                      reason: true,
                      outageStartDate: true,
                      outageEndDate: true,
                      startTime: true,
                      endTime: true,
                      isolations: true,
                      systemConstraints: true,
                      previousOutageRemarks: true,
                      outageType: true,
                    });

                    if (errors.site) {
                      scrollToField(siteRef.current);
                      return;
                    }

                    if (errors.person) {
                      scrollToField(personRef.current);
                      return;
                    }

                    if (errors.requisitionDate) {
                      scrollToField(requisitionDateRef.current);
                      return;
                    }

                    if (errors.unit) {
                      scrollToField(unitRef.current);
                      return;
                    }

                    if (errors.outageStartDate) {
                      scrollToField(outageStartDateRef.current);
                      return;
                    }

                    if (errors.outageEndDate) {
                      scrollToField(outageEndDateRef.current);
                      return;
                    }

                    if (errors.outageType) {
                      scrollToField(outageTypeRef.current);
                      return;
                    }

                    document.querySelector('form')?.requestSubmit();
                  }}
                  className="px-6 py-2 bg-red-600 text-white rounded-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[160px]"
                >
                  {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}

                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : isEditMode ? 'Update' : 'Add'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Styles */}
      <style>{`
        .form-input,
        .form-select {
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 14px;
          outline: none;
        }
          .form-input:focus,
.form-select:focus {
  outline: none;
  box-shadow: none;
  border: 1px solid #d1d5db;
}

        .form-select {
          appearance: none;
        }

        .select-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
          color: #6b7280;
          pointer-events: none;
        }

        .date-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
          color: #6b7280;
          cursor: pointer;
        }
       .error {
          font-size: 12px;
          color: #dc2626;
          margin-top: 4px;
        }

        .note-box {
          background: #f5f9ff;
          border-left: 4px solid #3b82f6;
          padding: 14px 16px;
          border-radius: 6px;
          font-size: 13px;
          color: #374151;
        }

        .note-title {
          font-weight: 600;
          margin-bottom: 6px;
        }

        .note-box p {
          margin-bottom: 4px;
        }

        .info-icon {
        display: inline-flex;
        align-items: center;
        }
      `}</style>
    </div>
  );
};

export default OutageRequisitionModal;
